"use client";

import {
  MediaMuteButton,
  MediaPlayButton,
  MediaTimeRange,
  MediaTimeDisplay,
  MediaFullscreenButton,
  MediaCaptionsButton,
  MediaControlBar,
} from "media-chrome/react";
import { MediaSettingsMenuButton } from "media-chrome/react/menu";
import { useMediaSelector } from "media-chrome/react/media-store";

import { cn } from "@marketing-sites/utils/cn";
import { useIsMobile } from "@marketing-sites/hooks/use-mobile";

import { FullscreenIcon, SoundOnIcon, OptionsIcon } from "./media-player.icons";
import { useHasPlayedOnce } from "./media-player.hooks";
import type { ControlsProps } from "./media-player.types";

export const Controls = ({
  controlsToShow,
  hideWhenPaused = false,
  className,
}: ControlsProps) => {
  const {
    play = true,
    mute = true,
    time = true,
    range = true,
    settings = true,
    fullscreen = true,
    captions = false,
  } = controlsToShow ?? {};

  const hasPlayedOnce = useHasPlayedOnce();
  const isMuted = useMediaSelector((state) => state?.mediaMuted);
  const isPaused = useMediaSelector((state) => state?.mediaPaused);

  const isMobile = useIsMobile();

  return (
    <MediaControlBar
      className={cn(
        "z-(--z-index-slot-controls) absolute bottom-0 left-0 right-0",
        "pointer-events-none hidden items-center justify-between gap-3 p-3 focus-within:flex",
        hasPlayedOnce && "pointer-events-auto",
        hasPlayedOnce && !(hideWhenPaused && isPaused) && "flex",
        // Portrait fullscreen letterboxes the video mid-screen, so bottom-0 would
        // strand the bar in the black band below it. 9/32 is half of 16:9's
        // height — the bar lands on the video's own bottom edge. Same offset the
        // older intercom-media-player applies as a translateY; this port dropped
        // it, leaving the bar across the middle of the picture (WS3-1083).
        isMobile &&
          "portrait:group-[[mediaisfullscreen]]/mediaplayercontroller:bottom-[calc(50%_-_(100vw_*_9_/_32))]! portrait:group-[[mediaisfullscreen]]/mediaplayercontroller:flex!",
        className,
      )}
    >
      {play && <MediaPlayButton />}
      {mute && (
        <MediaMuteButton>
          <div slot="icon" className="relative size-6">
            <SoundOnIcon />
            <div
              className={cn(
                "absolute inset-0 h-full w-full rotate-45",
                "after:absolute after:right-0 after:top-[calc(50%-1px)] after:h-[2px] after:w-0 after:bg-white",
                isMuted && "after:left-0 after:right-auto after:w-full",
              )}
            />
          </div>
        </MediaMuteButton>
      )}
      {time && <MediaTimeDisplay showDuration />}
      {range && <MediaTimeRange />}

      {captions && <MediaCaptionsButton />}

      {settings && (
        <MediaSettingsMenuButton className="group">
          <OptionsIcon slot="icon" />
        </MediaSettingsMenuButton>
      )}

      {fullscreen && (
        <MediaFullscreenButton>
          <FullscreenIcon slot="icon" />
        </MediaFullscreenButton>
      )}

      <div className="-z-1 bg-linear-to-t absolute inset-0 -mt-5 from-black/75 via-black/35 to-transparent opacity-75"></div>
    </MediaControlBar>
  );
};
